import { api } from './api.ts'

/**
 * Forme della stampa della scheda utente e dei loghi (462). Il foglio lo compone l'API
 * (apps/api/src/servizi/foglio-scheda.ts): la pagina lo impagina, il PDF/A lo riceve uguale.
 */

export type RigaFoglio = { nome: string; valore: string | null; mono?: boolean }
export type BloccoFoglio =
  | { tipo: 'sottotitolo'; testo: string }
  | { tipo: 'dati'; righe: RigaFoglio[] }
  | { tipo: 'elenco'; voci: string[] }
  | { tipo: 'nota'; testo: string }
export type SezioneFoglio = { titolo: string; blocchi: BloccoFoglio[] }

export type Foglio = {
  versione: 1
  modulo: {
    codice: string; titolo: string; revisione: string; data_revisione: string; titolo_sezione: string
    informativa: string; dichiarazione: string
    consensi: { tipo: string; testo: string; nota: string | null; facoltativo: boolean }[]
  }
  testata: { sede: string | null; progetto: string | null; anno: number; mese: string; numero_scheda: number }
  sezioni: SezioneFoglio[]
  firmatario: string | null
}

/** Logo per la stampa: `id` nullo e' il logo CIDIS dell'applicazione. */
export type LogoBreve = { id: string | null; nome: string; larghezza: number; altezza: number }

export type ProgettoStampa = {
  id: string; codice_cidis: string | null; titolo: string; aperto: boolean
  loghi: (LogoBreve & { id: string })[]
}

export type PdfRichiesto = {
  id: string
  stato: 'in_preparazione' | 'pronto' | 'scaduto' | 'non_riuscito'
  richiesto_il: string; completato_il: string | null; scade_il: string | null
  pagine: number | null; sha256: string | null; progetto: string | null; numero_scheda: number | null
}

/** Come `etichettaProgetto` dell'API: «codice · titolo». */
export const etichettaProgetto = (p: { codice_cidis: string | null; titolo: string }) =>
  `${p.codice_cidis ? `${p.codice_cidis} · ` : ''}${p.titolo}`

/** Indirizzo dell'immagine: i loghi caricati dall'API, sulla stessa origine; quello originale dai file dell'interfaccia. */
export const indirizzoLogo = (logo: { id: string | null }) =>
  (logo.id ? `/api/loghi/${logo.id}` : '/immagini/cidis-logo.png')

/** Chiave della scelta di un logo nelle caselle: l'identificativo, oppure «cidis» per l'originale. */
export const chiaveLogo = (logo: { id: string | null }) => logo.id ?? 'cidis'

export const DIMENSIONE_MASSIMA_LOGO = 2 * 1024 * 1024

/** Controllo prima dell'invio: lo stesso dell'API sul tipo dichiarato e sul peso, con le stesse parole. */
export function problemaFileLogo(file: File): string | null {
  if (file.type === 'image/svg+xml' || /\.svg$/i.test(file.name)) {
    return 'I file SVG non si possono usare: il PDF/A non li incorpora. Salva il logo come PNG.'
  }
  if (!['image/png', 'image/jpeg'].includes(file.type)) return 'Si possono caricare solo immagini PNG o JPEG.'
  if (file.size > DIMENSIONE_MASSIMA_LOGO) return 'Il file supera i 2 MB: riducilo e riprova.'
  return null
}

/**
 * Caricamento di un logo in tre tempi, come i documenti: prenotazione (`percorso` restituisce l'URL
 * prefirmato), invio diretto all'archivio con la cifratura lato server, conferma, che controlla il
 * file e lo mette in uso.
 */
export async function caricaLogo(percorso: string, nome: string, file: File): Promise<string> {
  const prenotazione = await api.post<{ id: string; url_caricamento: string }>(percorso, {
    nome, mime: file.type, dimensione: file.size,
  })
  const risposta = await fetch(prenotazione.url_caricamento, {
    method: 'PUT',
    headers: { 'content-type': file.type, 'x-amz-server-side-encryption': 'AES256' },
    body: file,
  })
  if (!risposta.ok) throw new Error('L’archivio non ha accettato il file: riprova fra qualche minuto.')
  await api.post(`/loghi/${prenotazione.id}/conferma`)
  return prenotazione.id
}
